import { useCallback, useLayoutEffect, useRef } from 'react';

import { useResizeObserver } from '@frontend/utils';

// Modal and drawer headers both render with Chakra's modal header class.
const HEADER_SELECTOR = '.chakra-modal__header';

/**
 * Measures the element the returned ref is attached to and publishes its width
 * to the enclosing modal/drawer header as the given CSS custom property, so the
 * theme can reserve matching space at the end of the header.
 */
export const useHeaderActionsWidth = <T extends HTMLElement>(
  cssVar: string,
) => {
  const elementRef = useRef<T>(null);

  const publishWidth = useCallback(() => {
    const element = elementRef.current;
    element
      ?.closest<HTMLElement>(HEADER_SELECTOR)
      ?.style.setProperty(cssVar, `${element.offsetWidth}px`);
  }, [cssVar]);

  // Measure before first paint, then keep it current via the observer below.
  useLayoutEffect(() => {
    publishWidth();

    const header = elementRef.current?.closest<HTMLElement>(HEADER_SELECTOR);
    return () => {
      header?.style.removeProperty(cssVar);
    };
  }, [publishWidth, cssVar]);

  useResizeObserver({ ref: elementRef, onResize: publishWidth });

  return elementRef;
};
